import { useNotification } from "@context/NotificationContext";
import type { PortServicesDocumentsPayload } from "@services/port-services/types";
import { sendPortServicesDocuments } from "@services/portServicesApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { t } from "i18next";
import { generatePrefixedNotifyList } from "../preview/utils";

const SERVICE = "bff-vessel-call";

interface UsePortServiceProps {
  portServiceId: string;
  notifyList: string[];
  name?: string;
  url: string;
  originalFileName?: string;
  expirationDate?: string | Date | null;
  setShouldRefetch: (value: boolean) => void;
  onClose: () => void;
  handleReset: () => void;
}

export interface UsePortServiceResult {
  onSubmit: () => Promise<void>;
}

export const useSendPortServiceDocument = (props: UsePortServiceProps): UsePortServiceResult => {
  const {
    portServiceId,
    name,
    url,
    originalFileName,
    expirationDate,
    notifyList,
    setShouldRefetch,
    onClose,
    handleReset,
  } = props;
  const { notify } = useNotification();

  const sendDocument = useMutation<unknown, unknown, PortServicesDocumentsPayload>({
    mutationFn: (payload) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "UploadPortServiceDocument",
          action: "send_port_service_document",
          entityType: "port_service_document",
        },
        (transactionId) =>
          sendPortServicesDocuments(SERVICE, payload, ...(transactionId ? [transactionId] : [])),
      ),
    onSuccess: () => {
      setShouldRefetch?.(true);
      onClose();
      handleReset();
      notify({
        title: t("vessel-call.port-services.notifications.service-added-success-title"),
        description: t("vessel-call.port-services.notifications.service-added-success-description"),
        type: "success",
      });
    },
    onError: (error) => {
      notify({
        title: t("vessel-call.port-services.notifications.service-added-error-title"),
        description: t("vessel-call.port-services.notifications.service-added-error-description"),
        type: "error",
      });
      console.error("Error submitting port service request:", error);
    },
  });

  const onSubmit = async (): Promise<void> => {
    const notificationFor = generatePrefixedNotifyList("FILE_UPLOADED", notifyList);
    const payload: PortServicesDocumentsPayload = {
      documents: [
        {
          portServiceId,
          name: name || originalFileName || "Document",
          type: "PORT_SERVICES",
          url,
          originalFileName,
          expirationDate: expirationDate
            ? new Date(expirationDate).toISOString().slice(0, 10)
            : null,
        },
      ],
      notificationFor,
    };
    sendDocument.mutate(payload);
  };
  return { onSubmit };
};
